import React from 'react';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const FilterPanel = ({ 
  filters, 
  onFilterChange, 
  resultCount, 
  onClearFilters,
  isOpen,
  onClose 
}) => {
  const locationOptions = [
    { value: 'all', label: 'All Locations' },
    { value: 'mumbai', label: 'Mumbai' },
    { value: 'delhi', label: 'Delhi NCR' },
    { value: 'bangalore', label: 'Bangalore' },
    { value: 'hyderabad', label: 'Hyderabad' },
    { value: 'pune', label: 'Pune' },
    { value: 'chennai', label: 'Chennai' },
    { value: 'kolkata', label: 'Kolkata' },
    { value: 'ahmedabad', label: 'Ahmedabad' }
  ];

  const industryOptions = [
    { value: 'all', label: 'All Industries' },
    { value: 'it', label: 'Information Technology' },
    { value: 'finance', label: 'Banking & Finance' },
    { value: 'healthcare', label: 'Healthcare' },
    { value: 'education', label: 'Education' },
    { value: 'ecommerce', label: 'E-commerce' },
    { value: 'manufacturing', label: 'Manufacturing' },
    { value: 'consulting', label: 'Consulting' },
    { value: 'marketing', label: 'Marketing & Advertising' }
  ];

  const jobTypeOptions = [
    { value: 'all', label: 'All Types' },
    { value: 'fulltime', label: 'Full Time' },
    { value: 'internship', label: 'Internship' },
    { value: 'parttime', label: 'Part Time' },
    { value: 'contract', label: 'Contract' }
  ];

  const experienceOptions = [
    { value: 'all', label: 'All Experience Levels' },
    { value: 'fresher', label: 'Fresher (0 years)' },
    { value: '0-1', label: '0-1 years' },
    { value: '1-3', label: '1-3 years' },
    { value: '3-5', label: '3-5 years' },
    { value: '5+', label: '5+ years' }
  ];

  const salaryRanges = [
    { value: 'all', label: 'All Salary Ranges' },
    { value: '0-3', label: '₹0 - ₹3 LPA' },
    { value: '3-5', label: '₹3 - ₹5 LPA' },
    { value: '5-8', label: '₹5 - ₹8 LPA' },
    { value: '8-12', label: '₹8 - ₹12 LPA' },
    { value: '12-15', label: '₹12 - ₹15 LPA' },
    { value: '15+', label: '₹15+ LPA' }
  ];

  const eligibilityOptions = [
    { id: 'btech', label: 'B.Tech/B.E.' },
    { id: 'mtech', label: 'M.Tech/M.E.' },
    { id: 'bca', label: 'BCA' },
    { id: 'mca', label: 'MCA' },
    { id: 'bsc', label: 'B.Sc.' },
    { id: 'msc', label: 'M.Sc.' },
    { id: 'bcom', label: 'B.Com' },
    { id: 'mba', label: 'MBA' }
  ];

  const handleEligibilityChange = (id, checked) => {
    const currentEligibility = filters?.eligibility || [];
    const newEligibility = checked
      ? [...currentEligibility, id]
      : currentEligibility?.filter(item => item !== id);
    onFilterChange('eligibility', newEligibility);
  };

  return (
    <div
      className={`fixed lg:static inset-y-0 left-0 z-40 w-80 bg-card border-r border-border overflow-y-auto transition-transform duration-250 ease-smooth lg:translate-x-0 ${
        isOpen ? 'translate-x-0' : '-translate-x-full'
      }`}
    >
      <div className="sticky top-0 bg-card border-b border-border p-4 lg:p-6 z-10">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-lg lg:text-xl font-heading font-semibold text-card-foreground">
            Filters
          </h2>
          <button
            onClick={onClose}
            className="lg:hidden p-2 rounded-md hover:bg-muted transition-smooth"
            aria-label="Close filters"
          >
            <Icon name="X" size={20} color="var(--color-muted-foreground)" />
          </button>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">
            {resultCount} {resultCount === 1 ? 'result' : 'results'} found
          </span>
          <button
            onClick={onClearFilters}
            className="text-primary hover:text-primary/80 font-medium transition-smooth"
          >
            Clear All
          </button>
        </div>
      </div>
      <div className="p-4 lg:p-6 space-y-6">
        <div>
          <Select
            label="Location"
            options={locationOptions}
            value={filters?.location || 'all'}
            onChange={(value) => onFilterChange('location', value)}
            searchable
            className="mb-0"
          />
        </div>

        <div>
          <Select
            label="Industry"
            options={industryOptions}
            value={filters?.industry || 'all'}
            onChange={(value) => onFilterChange('industry', value)}
            searchable
            className="mb-0"
          />
        </div>

        <div>
          <Select
            label="Job Type"
            options={jobTypeOptions}
            value={filters?.jobType || 'all'}
            onChange={(value) => onFilterChange('jobType', value)}
            className="mb-0"
          />
        </div>

        <div>
          <Select
            label="Experience Level"
            options={experienceOptions}
            value={filters?.experience || 'all'}
            onChange={(value) => onFilterChange('experience', value)}
            className="mb-0"
          />
        </div>

        <div>
          <Select
            label="Salary Range"
            options={salaryRanges}
            value={filters?.salary || 'all'}
            onChange={(value) => onFilterChange('salary', value)}
            className="mb-0"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-card-foreground mb-3">
            Eligibility Criteria
          </label>
          <div className="space-y-2">
            {eligibilityOptions?.map((option) => (
              <Checkbox
                key={option?.id}
                label={option?.label}
                checked={(filters?.eligibility || [])?.includes(option?.id)}
                onChange={(e) => handleEligibilityChange(option?.id, e?.target?.checked)}
                size="sm"
              />
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-card-foreground mb-3">
            Work Mode
          </label>
          <div className="space-y-2">
            <Checkbox
              label="Work from Home"
              checked={filters?.workFromHome || false}
              onChange={(e) => onFilterChange('workFromHome', e?.target?.checked)}
              size="sm"
            />
            <Checkbox
              label="Work from Office"
              checked={filters?.workFromOffice || false}
              onChange={(e) => onFilterChange('workFromOffice', e?.target?.checked)}
              size="sm"
            />
            <Checkbox
              label="Hybrid"
              checked={filters?.hybrid || false}
              onChange={(e) => onFilterChange('hybrid', e?.target?.checked)}
              size="sm"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-card-foreground mb-3">
            Posted Within
          </label>
          <div className="space-y-2">
            <Checkbox
              label="Last 24 hours"
              checked={filters?.posted24h || false}
              onChange={(e) => onFilterChange('posted24h', e?.target?.checked)}
              size="sm"
            />
            <Checkbox
              label="Last 7 days"
              checked={filters?.posted7d || false}
              onChange={(e) => onFilterChange('posted7d', e?.target?.checked)}
              size="sm"
            />
            <Checkbox
              label="Last 30 days"
              checked={filters?.posted30d || false}
              onChange={(e) => onFilterChange('posted30d', e?.target?.checked)}
              size="sm"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default FilterPanel;